import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Box, Divider, Group } from '@mantine/core'
import { AppButton, AppText } from '../../../app/common/ui'
import MyTextInput from '../../../app/common/form/MyTextInput'
import MyDateInput from '../../../app/common/form/MyDateInput'
import MySelectInput from '../../../app/common/form/MySelectInput'
import RichTextEditor from '../../../app/common/form/RichTextEditor'
import StarRatingInput from '../../../app/common/form/StarRatingInput'
import Breadcrumb from '../../../app/common/Breadcrumb'
import LoadingComponent from '../../../app/layout/LoadingComponents'
import { useStore } from '../../../app/stores/store'
import { FirearmFormValues } from '../../../app/models/firearm'
import PartFormModal from '../../inventory/PartFormModal'
import PartsBinPickerModal from '../../inventory/PartsBinPickerModal'
import PartsDisplay from '../../inventory/PartsDisplay'
import MobileImageCapture from '../../../app/common/imageUpload/MobileImageCapture'
import SerialBlurModal from '../../../app/common/imageUpload/SerialBlurModal'
import { ensureUploadableImageFile } from '../../../app/common/imageUpload/ensureUploadableImageFile'

const validationSchema = Yup.object({
  manufacturer: Yup.string().required('Manufacturer is required').max(200),
  model: Yup.string().required('Model is required').max(200),
  rating: Yup.number().min(1, 'Select 1–5 stars').max(5).required('Rating is required')
})

function cleanPayload (values: FirearmFormValues) {
  const clean = (v: unknown) => (v === '' || v === undefined ? undefined : v)
  const num = (v: unknown): number | undefined => {
    if (v === '' || v === undefined) return undefined
    const n = Number(v)
    return isNaN(n) ? undefined : n
  }
  return {
    ...values,
    firearmModelId: values.firearmModelId === '' || !values.firearmModelId ? null : values.firearmModelId,
    type: clean(values.type) as string | undefined,
    yearManufactured: num(values.yearManufactured),
    magazineCapacity: num(values.magazineCapacity),
    barrelTwistRate: clean(values.barrelTwistRate) as string | undefined,
    sightsType: clean(values.sightsType) as string | undefined,
    purchasePrice: num(values.purchasePrice),
    currentValue: num(values.currentValue),
    appraisalValue: num(values.appraisalValue),
    purchaseDate: clean(values.purchaseDate) as string | undefined,
    appraisalDate: clean(values.appraisalDate) as string | undefined,
    parts: (values.parts ?? []).map((p) => ({
      ...p,
      id: p.id || undefined,
      value: Number(p.value) || 0,
      installDate: clean(p.installDate) as string | undefined
    }))
  }
}

const TYPE_OPTIONS = [
  { key: 'handgun', value: 'Handgun', text: 'Handgun' },
  { key: 'rifle', value: 'Rifle', text: 'Rifle' },
  { key: 'shotgun', value: 'Shotgun', text: 'Shotgun' },
  { key: 'other', value: 'Other', text: 'Other' }
]

const CONDITION_OPTIONS = [
  { key: 'nib', value: 'NIB', text: 'NIB (New In Box)' },
  { key: 'excellent', value: 'Excellent', text: 'Excellent' },
  { key: 'verygood', value: 'Very Good', text: 'Very Good' },
  { key: 'good', value: 'Good', text: 'Good' },
  { key: 'fair', value: 'Fair', text: 'Fair' },
  { key: 'poor', value: 'Poor', text: 'Poor' }
]

const STEPS = [
  { label: 'Identity' },
  { label: 'Specifications' },
  { label: 'Condition' },
  { label: 'Value & Acquisition' },
  { label: 'Review & Notes' },
  { label: 'Media & Parts' }
]

export default observer(function FirearmForm () {
  const history = useHistory()
  const { firearmStore, firearmModelStore } = useStore()
  const { createFirearm, updateFirearm, loadFirearm, uploadImage, deleteImage, setMainImage, loadingInitial } = firearmStore
  const { loadModels, modelsByDate } = firearmModelStore
  const { id } = useParams<{ id: string }>()
  const [formValues, setFormValues] = useState<FirearmFormValues>(new FirearmFormValues())
  const [activeStep, setActiveStep] = useState(0)
  const [partModalOpen, setPartModalOpen] = useState(false)
  const [partsBinPickerOpen, setPartsBinPickerOpen] = useState(false)
  const [editingPartIndex, setEditingPartIndex] = useState<number | null>(null)
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null)
  const isEdit = !!id

  useEffect(() => {
    if (id) {
      loadFirearm(id).then((f) => f && setFormValues(new FirearmFormValues(f)))
    }
  }, [id, loadFirearm])

  useEffect(() => {
    loadModels()
  }, [loadModels])

  const firearmModelOptions = [
    { key: 'none', value: '', text: 'None (no link to registry)' },
    ...modelsByDate
      .filter((m) => m.status === 1)
      .map((m) => ({ key: m.id, value: m.id, text: `${m.make} ${m.model}` }))
  ]

  const handleSubmit = async (values: FirearmFormValues) => {
    const payload = cleanPayload({ ...values, id }) as FirearmFormValues
    if (!isEdit) {
      const newId = await createFirearm(payload)
      if (newId) history.push(`/manageFirearm/${newId}`)
    } else {
      await updateFirearm(payload)
      history.push(`/gun-vault/${id}`)
    }
  }

  const selectedFirearm = firearmStore.selectedFirearm
  const images = isEdit && selectedFirearm ? selectedFirearm.images : []

  const handleImageSelected = (file: File) => {
    setPendingImageFile(file)
  }

  const doUpload = async (file: File) => {
    const firearmId = id
    if (!firearmId) return
    let normalized: File
    try {
      normalized = await ensureUploadableImageFile(file)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not prepare image for upload.')
      return
    }
    try {
      await uploadImage(firearmId, normalized)
      setPendingImageFile(null)
    } catch {
      // toast / navigation via interceptor on failure
    }
  }

  if (loadingInitial && isEdit) {
    return <LoadingComponent content="Loading firearm..." />
  }

  const firearmLabel = isEdit && (selectedFirearm || (formValues.manufacturer && formValues.model))
    ? `${(selectedFirearm?.manufacturer ?? formValues.manufacturer) || ''} ${(selectedFirearm?.model ?? formValues.model) || ''}`.trim()
    : null
  const breadcrumbItems = [
    { label: 'Gun Vault', href: '/gun-vault' },
    isEdit && firearmLabel
      ? { label: firearmLabel, href: `/gun-vault/${id}` }
      : { label: 'Add firearm' }
  ]

  const canAdvanceStep0 = (values: FirearmFormValues) =>
    !!values.manufacturer?.trim() && !!values.model?.trim() && values.rating >= 1 && values.rating <= 5

  return (
    <Box sx={{ maxWidth: 760, margin: '0 auto' }}>
      <Breadcrumb items={breadcrumbItems} />
      <Group position="apart" mb="lg" mt="sm">
        <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, color: 'var(--gs-text)' }}>
          {isEdit ? 'EDIT FIREARM' : 'ADD TO GUN VAULT'}
        </AppText>
        <AppButton
          component={Link}
          to={isEdit ? `/gun-vault/${id}` : '/gun-vault'}
          variant="default"
          size="sm"
          sx={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          Cancel
        </AppButton>
      </Group>
      <Formik
        validationSchema={validationSchema}
        enableReinitialize
        initialValues={formValues}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, handleSubmit, isValid, isSubmitting, dirty }) => (
          <Form onSubmit={handleSubmit}>
            <Box
              sx={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}
              role="navigation"
              aria-label="Form steps"
            >
              {STEPS.map((step, index) => (
                <Box
                  key={index}
                  component="button"
                  type="button"
                  onClick={() => setActiveStep(index)}
                  disabled={index > activeStep}
                  aria-label={`Step ${index + 1}: ${step.label}`}
                  aria-current={index === activeStep ? 'step' : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: index === activeStep ? '1.5px solid var(--gs-accent)' : '1.5px solid var(--gs-border)',
                    background: index < activeStep ? 'rgba(232,93,4,0.08)' : 'var(--gs-surface)',
                    color: index === activeStep ? 'var(--gs-accent)' : index < activeStep ? 'var(--gs-accent)' : 'var(--gs-muted)',
                    cursor: index > activeStep ? 'not-allowed' : 'pointer',
                    opacity: index > activeStep ? 0.5 : 1,
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 13,
                    fontWeight: index === activeStep ? 600 : 400,
                    transition: 'border-color 150ms'
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700, minWidth: 16, textAlign: 'center' }}>{index + 1}</Box>
                  <Box component="span">{step.label}</Box>
                </Box>
              ))}
            </Box>

            <Box mt="md">
              {activeStep === 0 && (
                <>
                  <AppText size="sm" color="dimmed" weight={600} mb="md" transform="uppercase">
                    Identity
                  </AppText>
                  <MyTextInput name="manufacturer" placeholder="e.g. Glock" label="Manufacturer" />
                  <MyTextInput name="model" placeholder="e.g. 19 Gen5" label="Model" />
                  <MyTextInput name="countryOfOrigin" placeholder="e.g. Austria" label="Country of origin" />
                  <MySelectInput name="type" label="Type" options={TYPE_OPTIONS} placeholder="Select type" />
                  <MySelectInput
                    name="firearmModelId"
                    label="Link to community registry model (optional)"
                    options={firearmModelOptions}
                    placeholder="None (no link to registry)"
                  />
                  <StarRatingInput name="rating" label="Rating (1–5 stars)" />
                </>
              )}

              {activeStep === 1 && (
                <>
                  <AppText size="sm" color="dimmed" weight={600} mb="md" transform="uppercase">
                    Specifications
                  </AppText>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, '@media (max-width: 600px)': { gridTemplateColumns: '1fr' } }}>
                    <MyTextInput name="serialNumber" label="Serial number" placeholder="Optional" />
                    <MyTextInput name="caliber" label="Caliber" placeholder="e.g. 9mm" />
                    <MyTextInput name="barrelLength" label="Barrel length" placeholder="e.g. 4.5 in" />
                    <MyTextInput name="weight" label="Weight" placeholder="e.g. 24 oz" />
                    <MyTextInput name="yearManufactured" type="number" label="Year manufactured" placeholder="e.g. 2020" />
                    <MyTextInput name="actionType" label="Action type" placeholder="e.g. Semi-auto" />
                    <MyTextInput name="finishType" label="Finish type" placeholder="e.g. Nitride" />
                    <MyTextInput name="stockType" label="Stock type" placeholder="e.g. Polymer" />
                    <MyTextInput name="magazineCapacity" type="number" label="Magazine capacity (rounds)" placeholder="e.g. 15" />
                    <MyTextInput name="barrelTwistRate" label="Barrel twist rate" placeholder="e.g. 1:7" />
                    <MyTextInput name="sightsType" label="Sights type" placeholder="e.g. Iron, Red dot" />
                  </Box>
                </>
              )}

              {activeStep === 2 && (
                <>
                  <AppText size="sm" color="dimmed" weight={600} mb="md" transform="uppercase">
                    Condition
                  </AppText>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, '@media (max-width: 600px)': { gridTemplateColumns: '1fr' } }}>
                    <MySelectInput name="conditionGrade" label="Condition grade" options={CONDITION_OPTIONS} placeholder="Select" />
                    <MyTextInput name="boreCondition" label="Bore condition" placeholder="Optional" />
                    <MyTextInput name="stockCondition" label="Stock condition" placeholder="Optional" />
                    <MyTextInput name="mechanicalCondition" label="Mechanical condition" placeholder="Optional" />
                  </Box>
                </>
              )}

              {activeStep === 3 && (
                <>
                  <AppText size="sm" color="dimmed" weight={600} mb="md" transform="uppercase">
                    Value & Acquisition
                  </AppText>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, '@media (max-width: 600px)': { gridTemplateColumns: '1fr' } }}>
                    <MyTextInput name="purchasePrice" type="number" label="Purchase price ($)" placeholder="0.00" />
                    <MyDateInput name="purchaseDate" label="Purchase date" placeholderText="Optional" />
                    <MyTextInput name="currentValue" type="number" label="Current value ($)" placeholder="0.00" />
                    <MyTextInput name="appraisalValue" type="number" label="Appraisal value ($)" placeholder="0.00" />
                    <MyDateInput name="appraisalDate" label="Appraisal date" placeholderText="Optional" />
                    <MyTextInput name="locationStored" label="Location stored" placeholder="e.g. Safe 1" />
                    <MyTextInput name="acquisitionType" label="Acquisition type" placeholder="e.g. Purchase" />
                  </Box>
                </>
              )}

              {activeStep === 4 && (
                <>
                  <AppText size="sm" color="dimmed" weight={600} mb="md" transform="uppercase">
                    Review & Notes
                  </AppText>
                  <RichTextEditor name="thoughtsAndOpinions" label="Thoughts & opinions" placeholder="Your thoughts and opinions..." />
                  <RichTextEditor name="review" label="Review" placeholder="Conclusive review..." />
                  <RichTextEditor name="notableThoughts" label="Notable thoughts" placeholder="Notable thoughts..." />
                  <MyTextInput name="inventoryNotes" label="Inventory notes" placeholder="Optional notes" />
                </>
              )}

              {activeStep === 5 && (
                <>
                  <AppText size="sm" color="dimmed" weight={600} mb="md" transform="uppercase">
                    Media & Parts
                  </AppText>
                  {isEdit && id && (
                    <Box mb="lg">
                      <AppText size="sm" weight={500} mb="xs">Images</AppText>
                      <Box mb="sm">
                        <MobileImageCapture
                          onFile={handleImageSelected}
                          label="Upload image"
                          accept="image/*"
                        />
                      </Box>
                      <SerialBlurModal
                        file={pendingImageFile}
                        onConfirm={doUpload}
                        onSkip={doUpload}
                        onClose={() => setPendingImageFile(null)}
                      />
                      {images.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {images.map((img) => (
                            <Box
                              key={img.id}
                              sx={{
                                position: 'relative',
                                width: 100,
                                border: img.isMain ? '2px solid var(--gs-accent)' : '1px solid var(--gs-border)',
                                borderRadius: 6,
                                overflow: 'hidden'
                              }}
                            >
                              <img src={img.url} alt="" style={{ width: '100%', height: 72, objectFit: 'cover', display: 'block' }} />
                              {img.isMain && (
                                <Box sx={{ position: 'absolute', top: 2, right: 2, background: 'var(--gs-accent)', color: '#fff', fontSize: 8, fontWeight: 700, padding: '1px 4px', borderRadius: 2 }}>
                                  Main
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', gap: 4, padding: 4 }}>
                                {!img.isMain && (
                                  <AppButton type="button" size="xs" variant="subtle" onClick={() => setMainImage(id, img.id)}>
                                    Set main
                                  </AppButton>
                                )}
                                <AppButton type="button" size="xs" variant="subtle" color="red" onClick={() => deleteImage(id, img.id)}>
                                  Delete
                                </AppButton>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                  <Divider label="Custom parts" labelPosition="center" my="md" />
                  <PartsDisplay
                    parts={values.parts ?? []}
                    totalValue={(values.parts ?? []).reduce((sum, p) => sum + (p.value || 0), 0)}
                    editable
                    onAdd={() => {
                      setEditingPartIndex(null)
                      setPartModalOpen(true)
                    }}
                    onAddFromBin={() => setPartsBinPickerOpen(true)}
                    onEdit={(part, index) => {
                      setEditingPartIndex(index)
                      setPartModalOpen(true)
                    }}
                    onDelete={(index) => {
                      const next = [...(values.parts ?? [])]
                      next.splice(index, 1)
                      setFieldValue('parts', next)
                    }}
                  />
                  <PartFormModal
                    opened={partModalOpen}
                    onClose={() => {
                      setPartModalOpen(false)
                      setEditingPartIndex(null)
                    }}
                    onSave={(part) => {
                      const next = [...(values.parts ?? [])]
                      if (editingPartIndex !== null) {
                        next[editingPartIndex] = part
                      } else {
                        next.push(part)
                      }
                      setFieldValue('parts', next)
                    }}
                    initialPart={editingPartIndex !== null ? values.parts?.[editingPartIndex] : null}
                  />
                  <PartsBinPickerModal
                    opened={partsBinPickerOpen}
                    onClose={() => setPartsBinPickerOpen(false)}
                    existingParts={values.parts ?? []}
                    onSelectParts={(newParts) =>
                      setFieldValue('parts', [...(values.parts ?? []), ...newParts])
                    }
                  />
                </>
              )}
            </Box>

            <Group position="apart" mt="xl">
              <AppButton
                type="button"
                variant="default"
                disabled={activeStep === 0}
                onClick={() => setActiveStep((s) => s - 1)}
              >
                Previous
              </AppButton>
              {activeStep < 5
                ? (
                <AppButton
                  type="button"
                  variant="filled"
                  color="red"
                  disabled={activeStep === 0 && !canAdvanceStep0(values)}
                  onClick={() => setActiveStep((s) => s + 1)}
                >
                  Next
                </AppButton>
                  )
                : (
                <AppButton
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting || !isValid || (!isEdit && !dirty)}
                  sx={{ background: 'var(--gs-accent)', color: '#fff', '&:hover': { background: '#bf4a00' } }}
                >
                  {isEdit ? 'Save changes' : 'Add to vault'}
                </AppButton>
                  )}
            </Group>
          </Form>
        )}
      </Formik>
    </Box>
  )
})
